(function () {
  const STEP_ATTR = 'data-wizard-step';

  class ConfiguratorWizard {
    constructor(root) {
      this.root = root;
      this.railHost = root.querySelector('[data-wizard-rail]');
      this.navHost = root.querySelector('[data-wizard-nav]');
      this.submit = root.querySelector('[data-submit]');
      this.summaryHost = root.querySelector('[data-wizard-summary]');
      this.resetBtn = root.querySelector('[data-reset]');
      this.scrollable = root.querySelector('.drawer__scrollable');

      // Sections forming one exclusive choice (every option carrying the
      // same exclusion group, e.g. the two paint families in Exterior
      // Finishes) collapse into the later section, so pick-one reads as
      // one step. The merged step takes the exclusion collection's title
      // when the options carry one.
      const allSections = Array.from(root.querySelectorAll('[data-section]'));
      const groupOf = (sec) => {
        const opts = sec.querySelectorAll('[data-option]');
        if (!opts.length) return '';
        let g = null;
        for (const o of opts) {
          const v = (o.getAttribute('data-exclusion-groups') || '').trim();
          if (!v) return '';
          if (g === null) g = v;
          else if (g !== v) return '';
        }
        return g || '';
      };
      const headingOf = (sec) => {
        const el = sec.querySelector('.section-options__title');
        return el ? el.textContent.trim() : '';
      };
      const unitOf = (sec) => sec.querySelector('.wz-swatchgrid') || sec.querySelector('.section-options__body');
      // A family block keeps each source section's options under its own
      // label, so a merged card reads as stacked choices (Protek above,
      // stain below, preserver riding at the bottom) rather than one
      // anonymous grid where twin sides duplicate every colour.
      // Tins and factory finishes look identical as swatches, so each
      // family says which it is. Ready Painted is applied before delivery;
      // everything else in these cards ships as a tin the customer applies.
      const noteFor = (heading) => {
        if (/^ready painted/i.test(heading)) return 'Your building arrives painted. Allow 6 - 8 weeks.';
        if (/royal exterior|stain|paint|preserv/i.test(heading)) return 'Supplied as a tin for you to apply.';
        return '';
      };
      const familyOf = (sec, addon) => {
        const unit = unitOf(sec);
        if (!unit) return null;
        const fam = document.createElement('div');
        fam.className = 'wz-family' + (addon ? ' wz-family--addon' : '');
        const lab = document.createElement('div');
        lab.className = 'wz-family__label';
        lab.textContent = headingOf(sec);
        fam.appendChild(lab);
        const note = noteFor(headingOf(sec));
        if (note) {
          const noteEl = document.createElement('div');
          noteEl.className = 'wz-family__note';
          noteEl.textContent = note;
          fam.appendChild(noteEl);
        }
        fam.appendChild(unit);
        // Each family gets its own "More information" row under its own
        // swatches. The card's single Liquid link sat below the last family
        // (usually the preserver) while opening whichever swatch was ticked
        // first (usually a paint), so it read as belonging to the wrong one.
        // The shared link is hidden in stacked cards (wizard CSS).
        const infoText = sec.querySelector('.wz-swatchgroup__info span');
        const info = document.createElement('button');
        info.type = 'button';
        info.className = 'wz-family__info';
        info.hidden = true;
        info.innerHTML =
          '<span class="pointer-events-none"></span>' +
          '<svg class="pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>';
        info.querySelector('span').textContent = (infoText && infoText.textContent.trim()) || 'More information';
        fam.appendChild(info);
        return fam;
      };
      const retitle = (sec) => {
        const titleEl = sec.querySelector('.section-options__title');
        const titled = sec.querySelector('[data-option][data-exclusion-title]');
        if (titleEl && titled) titleEl.textContent = titled.getAttribute('data-exclusion-title');
      };
      const stackUp = (host, list) => {
        const anchorUnit = unitOf(host);
        if (!anchorUnit) return null;
        const parent = anchorUnit.parentElement;
        const anchor = anchorUnit.nextSibling;
        const stack = document.createElement('div');
        stack.className = 'wz-famstack';
        const kicker = document.createElement('div');
        kicker.className = 'wz-famstack__kicker';
        kicker.textContent = 'Choose one from:';
        stack.appendChild(kicker);
        list.forEach((sec) => {
          const fam = familyOf(sec);
          if (fam) stack.appendChild(fam);
          if (sec !== host) {
            sec.hidden = true;
            sec.setAttribute('data-wizard-merged', '');
          }
        });
        parent.insertBefore(stack, anchor);
        return stack;
      };
      const byGroup = {};
      allSections.forEach((s) => {
        const g = groupOf(s);
        if (g) (byGroup[g] = byGroup[g] || []).push(s);
      });
      Object.keys(byGroup).forEach((g) => {
        const list = byGroup[g];
        if (list.length < 2) return;
        list.sort((a, b) => this.order(a) - this.order(b));
        const target = list[list.length - 1];
        // Classify before stackUp: it moves every swatch grid into the host,
        // so a DOM test afterwards would see the host as the only swatch
        // section and call every merged colour card mixed.
        const isSwatches = (s) => s.getAttribute('data-style') === 'Swatch';
        const mixed = list.some(isSwatches) && !list.every(isSwatches);
        stackUp(target, list);
        // Twin instances of one card ("Ready Painted - Shed on the Left" /
        // "... Right") title the step by their shared base name. Mixed
        // families fall back to the exclusion collection's title, which is
        // also what a treatment service joining this pool would produce.
        const bases = list.map((s) => headingOf(s).split(' - ')[0].trim());
        const shared = bases[0] && bases.every((b) => b === bases[0]);
        if (shared) {
          const titleEl = target.querySelector('.section-options__title');
          if (titleEl) titleEl.textContent = bases[0];
        } else {
          retitle(target);
        }

        // A service card (a checkbox list such as Pressure Treatment) pooled
        // with colour swatches: the colour header ("Choose a colour", priced
        // from the paints only) does not describe that choice, so the stacked
        // families speak instead, each colour family carrying its own price.
        // Sizes differ in which families they offer, so syncMixedCards
        // settles this on every render. A collection named after just one
        // of the families would title the step after that family alone, so
        // name both instead.
        if (mixed) {
          target.setAttribute('data-wizard-mixed', '');
          target.querySelectorAll('.wz-famstack .wz-family').forEach((fam) => {
            const lab = fam.querySelector('.wz-family__label');
            if (!lab || !fam.querySelector('[data-swatch-option]')) return;
            const tag = document.createElement('span');
            tag.className = 'wz-family__price';
            lab.appendChild(tag);
          });
          const titleEl = target.querySelector('.section-options__title');
          const names = Array.from(new Set(bases.filter(Boolean)));
          if (titleEl && names.length > 1 && names.some((n) => n.toLowerCase() === titleEl.textContent.trim().toLowerCase())) {
            titleEl.textContent = names.join(' or ');
          }
        }
      });

      // Wood preserver joins the finishes card as an independent extra
      // instead of standing as its own step. Its options carry no exclusion
      // group, so the base configurator already lets it sit checked next to
      // a paint choice; this only moves it into the card. Ready Painted
      // cards never host it: painted buildings are quoted separately.
      const preservers = allSections.filter(
        (s) =>
          !s.hasAttribute('data-wizard-merged') &&
          /preserv/i.test(headingOf(s)) &&
          !groupOf(s) &&
          s.querySelector('[data-option]')
      );
      if (preservers.length) {
        const hosts = allSections.filter(
          (s) =>
            !s.hasAttribute('data-wizard-merged') &&
            // A treatment-or-paint card is a Ready Painted host renamed after
            // its collection, so it never takes the preserver either.
            !s.hasAttribute('data-wizard-mixed') &&
            groupOf(s) &&
            /exterior|stain|paint/i.test(headingOf(s)) &&
            !/ready painted/i.test(headingOf(s))
        );
        const host = hosts[hosts.length - 1];
        if (host) {
          let stack = host.querySelector('.wz-famstack');
          if (!stack) {
            // Single paint family, so no merge ran: build the stack around
            // it and let the card take the collection title the merged form
            // would carry (e.g. Exterior Finishes).
            stack = stackUp(host, [host]);
            retitle(host);
          }
          if (stack) {
            preservers.forEach((p) => {
              const fam = familyOf(p, true);
              if (!fam) return;
              stack.appendChild(fam);
              p.hidden = true;
              p.setAttribute('data-wizard-merged', '');
            });
          }
        }
      }

      // The Liquid header quotes the card's own first option, which on a
      // stacked card is whichever family happened to render last. Requote
      // as the cheapest visible colour, marked "from" when families price
      // differently; a selection overwrites it with the real price.
      allSections.forEach((sec) => {
        if (!sec.querySelector('.wz-famstack')) return;
        const host = sec.querySelector('[data-selected-option-price]');
        if (!host || sec.querySelector('[data-swatch-option] input:checked')) return;
        const prices = Array.from(sec.querySelectorAll('[data-swatch-option]:not(.hidden)'))
          .map((s) => parseInt(s.dataset.price, 10))
          .filter((n) => !isNaN(n));
        if (!prices.length) return;
        const pretty = this.money(Math.min.apply(null, prices));
        if (pretty) host.textContent = (new Set(prices).size > 1 ? 'from ' : '') + '+' + pretty;
      });

      this.steps = Array.from(root.querySelectorAll('[data-section]:not([data-wizard-merged])'))
        .map((step, i) => ({ step, i }))
        .sort((a, b) => this.order(a.step) - this.order(b.step) || a.i - b.i)
        .map((x) => x.step);

      if (!this.railHost || !this.navHost || this.steps.length < 2) return;

      const strings = root.querySelector('[data-wizard-a11y]');
      try {
        this.t = strings ? JSON.parse(strings.textContent) : {};
      } catch (e) {
        this.t = {};
      }

      this.index = 0;
      this.build();
      this.bind();
      this.render();

      root.classList.add('is-wizard-ready');
    }

    heading(step) {
      const el = step.querySelector('.section-options__title');
      return el ? el.textContent.trim() : '';
    }

    order(step) {
      const n = parseInt(step.getAttribute('data-wizard-order'), 10);
      return isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
    }

    title(step, i) {
      return this.heading(step) || 'Step ' + (i + 1);
    }

    options(step) {
      return Array.from(step.querySelectorAll('[data-option]'));
    }

    // Pick-a-day's Delivery day step (snippets/pick-a-day-step.liquid, only
    // rendered with the theme setting on). It has no [data-option], so the
    // option-based answers below are special-cased for it; root.padStep
    // (assets/pick-a-day.js) knows whether a day is held.
    isDay(step) {
      return !!step && step.hasAttribute('data-day-step');
    }

    visible(step) {
      if (this.isDay(step)) return !step.hidden;
      return this.options(step).some((o) => !o.classList.contains('hidden'));
    }

    required(step) {
      return step.getAttribute('data-required') === 'true';
    }

    groupUnitPrice(step) {
      const first = this.options(step).filter((o) => !o.classList.contains('hidden'))[0];
      return first ? parseInt(first.dataset.price, 10) || 0 : 0;
    }

    syncFreeGroups() {
      const label = (this.summaryHost && this.summaryHost.dataset.freeLabel) || 'Free';

      this.steps.forEach((step) => {
        if (!this.freeGroup(step)) return;

        const host = step.querySelector('[data-selected-option-price]');
        if (!host || host.querySelector('.wz-free-pill')) return;

        const pence = this.groupUnitPrice(step);
        if (!pence) return;

        host.textContent = '';
        host.classList.add('is-free');

        const was = document.createElement('s');
        was.className = 'wz-free-was';
        was.setAttribute('aria-hidden', 'true');
        was.textContent = this.money(pence) || '';

        const pill = document.createElement('span');
        pill.className = 'wz-free-pill';
        pill.textContent = label;

        host.appendChild(was);
        host.appendChild(pill);
      });
    }

    freeGroup(step) {
      return step.getAttribute('data-free') === 'true';
    }

    freeAllowance() {
      let pence = 0;

      this.steps.forEach((step) => {
        if (!this.freeGroup(step)) return;

        const picked = this.options(step).filter((o) => {
          if (o.classList.contains('hidden') || o.hasAttribute('data-gift')) return false;
          const input = o.querySelector('input[type="checkbox"], input[type="radio"]');
          return input && input.checked;
        });

        if (picked.length) pence += parseInt(picked[0].dataset.price, 10) || 0;
      });

      return pence;
    }

    chosen(step) {
      if (this.isDay(step)) return !!(this.root.padStep && this.root.padStep.done());
      return this.options(step).some((o) => {
        if (o.classList.contains('hidden')) return false;
        const input = o.querySelector('input[type="checkbox"], input[type="radio"]');
        return input && input.checked;
      });
    }

    live() {
      return this.steps.map((s, i) => (this.visible(s) ? i : -1)).filter((i) => i > -1);
    }

    isLast() {
      const live = this.live();
      return live.length === 0 || this.index >= live[live.length - 1];
    }

    selectable(step) {
      if (this.isDay(step)) return false;
      return this.options(step).some(
        (o) => !o.classList.contains('hidden') && o.querySelector('input[type="checkbox"], input[type="radio"]')
      );
    }

    canLeave(i) {
      const step = this.steps[i];
      if (!step) return true;
      if (!this.visible(step)) return true;
      // The engine puts a notice in any step holding a paid required option
      // or one of its triggers while that option is missing: the shopper
      // adds it or removes a trigger before moving on.
      if (step.querySelector('.pcfg-required')) return false;
      if (!this.required(step)) return true;

      if (!this.selectable(step)) return true;

      return this.chosen(step);
    }

    build() {
      this.steps.forEach((step, i) => {
        step.setAttribute(STEP_ATTR, i);

        const header = step.querySelector('.section-options__header');
        const title = step.querySelector('.section-options__title');
        if (header && title && !header.querySelector('.wizard-step__count')) {
          const count = document.createElement('span');
          count.className = 'wizard-step__count';
          count.id = 'wizard-step-count-' + this.root.id + '-' + i;
          header.insertBefore(count, title);
          step.wizardCount = count;

          if (!title.id) title.id = 'wizard-step-title-' + this.root.id + '-' + i;
          title.setAttribute('aria-labelledby', title.id + ' ' + count.id);
        }
      });

      this.railHost.innerHTML = this.steps
        .map(
          (step, i) =>
            '<button class="wizard-rail__item" type="button" data-wizard-goto="' +
            i +
            '">' +
            '<span class="wizard-rail__track"><span class="wizard-rail__fill"></span></span>' +
            '<span class="wizard-rail__label">' +
            '<span class="wizard-rail__num">' + (i + 1) + '.</span>' +
            '<span class="wizard-rail__tick" aria-hidden="true">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5l5 5L20 6.5"/></svg>' +
            '</span>' +
            '<span class="wizard-rail__name">' +
            this.title(step, i) +
            '</span></span>' +
            '</button>'
        )
        .join('');
      this.railHost.hidden = false;

      this.buildDefaults();

      this.navHost.innerHTML =
        '<span class="wizard-nav__hint" data-wizard-hint role="status" aria-live="polite"></span>' +
        '<div class="wizard-nav__buttons">' +
        '<button class="button button--secondary button--lg wizard-nav__back" type="button" is="hover-button" data-wizard-back>' +
        '<span class="btn-fill" data-fill></span><span class="btn-text"></span></button>' +
        '<button class="button button--primary button--lg wizard-nav__next" type="button" is="hover-button" data-wizard-next>' +
        '<span class="btn-fill" data-fill></span><span class="btn-text"></span></button>' +
        '</div>';
      this.navHost.hidden = false;

      if (this.summaryHost) this.summaryHost.hidden = false;

      this.finance = this.root.querySelector('.wizard-finance-area') || this.root.querySelector('.wizard-finance');
      if (this.finance) this.finance.remove();

      const buttons = this.navHost.querySelector('.wizard-nav__buttons');
      if (this.submit && buttons) buttons.appendChild(this.submit);
    }

    bind() {
      this.railHost.addEventListener('click', (e) => {
        const b = e.target.closest('[data-wizard-goto]');
        if (b && !b.disabled) this.go(parseInt(b.dataset.wizardGoto, 10));
      });

      this.navHost.addEventListener('click', (e) => {
        if (e.target.closest('[data-wizard-next]')) this.go(this.nextIndex());
        if (e.target.closest('[data-wizard-back]')) this.go(this.prevIndex());
      });

      if (this.summaryHost) {
        this.summaryHost.addEventListener('click', (e) => {
          const jump = e.target.closest('[data-summary-goto]');
          if (jump) this.go(parseInt(jump.dataset.summaryGoto, 10));
        });
      }

      this.root.addEventListener('click', (e) => {
        const info = e.target.closest('.wz-family__info');
        if (!info || !info.dataset.infoTab) return;
        // Reuse the engine's own opener: point the card's original trigger
        // at this family's ticked swatch and click it.
        const section = info.closest('[data-section]');
        // Never a preserver card's own trigger, which sits above this one.
        const trigger = section && Array.from(section.querySelectorAll('[data-target-tab]')).find((el) => !el.closest('[data-option]'));
        if (!trigger) return;
        trigger.dataset.targetTab = info.dataset.infoTab;
        trigger.click();
      });

      this.root.addEventListener('change', () => this.schedule());
      this.root.addEventListener('click', () => this.schedule());

      // Add to cart refused by the engine: take the shopper to the first step
      // holding the notice so they can see what to add or remove.
      this.root.addEventListener('configurator:requirements-unmet', () => {
        const target = this.steps.findIndex((step) => step.querySelector('.pcfg-required'));
        if (target === -1) return;
        if (target !== this.index) {
          this.index = target;
          this.render();
          this.focusStep();
        }
        this.flag(target);
        const notice = this.steps[target].querySelector('.pcfg-required');
        if (notice) notice.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });

      // A size change swaps which options are visible and the base prices
      // without necessarily firing a change inside the drawer, so re-render
      // for empty families and the summary's compare price.
      if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
        theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, () => this.schedule());
      }

      if (this.resetBtn) {
        this.resetBtn.addEventListener('click', () => {
          this.index = 0;
          this.reachedEnd = false;
          this.root.classList.remove('has-reached-end');
          this.schedule();
        });
      }

      const text = this.submit && this.submit.querySelector('.btn-text');
      if (text) {
        this.totalObserver = new MutationObserver(() => this.syncTotal());
        this.totalObserver.observe(text, { childList: true, characterData: true, subtree: true });
      }
    }

    buildDefaults() {
      this.steps.forEach((step) => {
        const label = step.getAttribute('data-default-label');
        if (!label) return;

        const body = step.querySelector('.section-options__body');
        if (!body || body.querySelector('.wz-default')) return;

        const note = step.getAttribute('data-default-note') || '';
        const free = step.getAttribute('data-default-free') || 'No extra cost';
        const grid = step.querySelector('.wz-swatchgrid');
        const el = document.createElement('div');

        if (grid) {
          el.className = 'wz-swatch wz-default wz-default--swatch';
          el.innerHTML =
            '<button class="wz-swatch__card" type="button" aria-pressed="true">' +
            '<span class="wz-swatch__chip wz-default__chip"></span>' +
            '<span class="wz-swatch__name"></span>' +
            '</button>';
          el.querySelector('.wz-swatch__name').textContent = label;

          el.querySelector('button').addEventListener('click', () => {
            this.clearStep(step);
          });

          grid.insertBefore(el, grid.firstChild);
          return;
        }

        el.className = 'wz-opt wz-default wz-default--card';
        el.innerHTML =
          '<button class="wz-opt__card" type="button" aria-pressed="true">' +
          '<span class="wz-opt__media"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<path d="M3 8l9-5 9 5v8l-9 5-9-5z"/><path d="M3 8l9 5 9-5M12 13v8"/></svg></span>' +
          '<span class="wz-opt__body">' +
          '<span class="wz-opt__title"></span>' +
          (note ? '<span class="wz-opt__desc"></span>' : '') +
          '<span class="wz-opt__foot"><span class="wz-opt__price wz-opt__price--free"></span></span>' +
          '</span>' +
          '<span class="wz-opt__meta"><span class="wz-opt__mark">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5l5 5L20 6.5"/></svg>' +
          '</span></span></button>';

        el.querySelector('.wz-opt__title').textContent = label;
        if (note) el.querySelector('.wz-opt__desc').textContent = note;
        el.querySelector('.wz-opt__price').textContent = free;

        el.querySelector('button').addEventListener('click', () => this.clearStep(step));

        body.insertBefore(el, body.firstChild);
      });
    }

    clearStep(step) {
      this.options(step).forEach((o) => {
        const input = o.querySelector('input[type="checkbox"], input[type="radio"]');
        if (input && input.checked) {
          input.checked = false;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      this.schedule();
    }

    syncSelected() {
      this.steps.forEach((step) => {
        this.options(step).forEach((o) => {
          const input = o.querySelector('input[type="checkbox"], input[type="radio"]');
          o.classList.toggle('is-selected', !!(input && input.checked));
        });
      });
    }

    syncSummary() {
      if (!this.summaryHost) return;

      const rows = [];
      const freed = {};
      this.steps.forEach((step, stepIndex) => {
        const group = this.title(step, stepIndex);
        this.options(step).forEach((o) => {
          if (o.classList.contains('hidden')) return;
          const input = o.querySelector('input[type="checkbox"], input[type="radio"]');
          if (!input || !input.checked) return;

          const titleEl = o.querySelector('.wz-opt__title');
          const name = titleEl ? titleEl.textContent.trim() : (o.dataset.name || '').trim();
          const pence = parseInt(o.dataset.price, 10) || 0;
          const was = parseInt(o.dataset.compare, 10) || 0;

          // A free gift (data-gift, see the engine's updatePrice) reads
          // free without using up its group's free pick
          const gift = o.hasAttribute('data-gift');
          const free = gift || (this.freeGroup(step) && !freed[group]);
          if (free && !gift) freed[group] = true;

          if (name) {
            rows.push({
              group,
              name,
              pence: free ? 0 : pence,
              was: free ? pence : was > pence ? was : 0,
              free: free,
              stepIndex,
            });
          }
        });
      });

      const base = (this.root.activeVariant && this.root.activeVariant.price) || 0;
      const d = this.summaryHost.dataset;
      const money = (p) => this.money(p) || String(p / 100);
      const total = rows.reduce((sum, r) => sum + r.pence, base);

      const chevron =
        '<svg class="wz-summary__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>';

      const row = (interactive, stepIndex) =>
        '<li class="wz-summary__item">' +
        (interactive
          ? '<button class="wz-summary__row wz-summary__row--link" type="button" data-summary-goto="' + stepIndex + '">'
          : '<div class="wz-summary__row">') +
        '<span class="wz-summary__label">' +
        (interactive ? '<span class="wz-summary__group"></span>' : '') +
        '<span class="wz-summary__name"></span></span>' +
        '<span class="wz-summary__money">' +
        '<s class="wz-summary__was"></s>' +
        '<span class="wz-summary__price"></span>' +
        '</span>' +
        (interactive ? chevron : '') +
        (interactive ? '</button>' : '</div>') +
        '</li>';

      // The summary body is rebuilt on every render, but the finance area is
      // parked after it exactly once: it hosts Zopa's widget, a custom
      // element whose SDK does not survive being re-parented.
      let summaryBody = this.summaryHost.querySelector('[data-wz-body]');
      if (!summaryBody) {
        this.summaryHost.textContent = '';
        summaryBody = document.createElement('div');
        summaryBody.setAttribute('data-wz-body', '');
        this.summaryHost.appendChild(summaryBody);
      }

      summaryBody.innerHTML =
        '<span class="wz-summary__heading"></span>' +
        '<ul class="wz-summary__list">' +
        row(false) +
        rows.map((r) => row(true, r.stepIndex)).join('') +
        '</ul>' +
        '<div class="wz-summary__total"><span></span><b></b></div>' +
        '<div class="wz-summary__saving"><span></span><b></b></div>' +
        '<p class="wz-summary__note"></p>';

      this.summaryHost.querySelector('.wz-summary__heading').textContent = d.summaryTitle || '';
      const items = this.summaryHost.querySelectorAll('.wz-summary__row');
      // The Liquid compare price belongs to the variant the page loaded
      // with; after a size change the engine's active variant carries the
      // right one, so the struck price and "You save" follow the size.
      const activeVariant = this.root.activeVariant;
      const baseWas = activeVariant && 'compare_at_price' in activeVariant
        ? parseInt(activeVariant.compare_at_price, 10) || 0
        : parseInt(d.productCompare, 10) || 0;

      const setMoney = (el, now, was) => {
        const s = el.querySelector('.wz-summary__was');
        s.textContent = was > now ? money(was) : '';
        s.hidden = !(was > now);
        s.setAttribute('aria-hidden', 'true');
        el.querySelector('.wz-summary__price').textContent = now === 0 ? '' : money(now);
        el.classList.toggle('is-reduced', was > now);
      };

      items[0].querySelector('.wz-summary__name').textContent = d.productTitle || '';
      setMoney(items[0], base, baseWas);

      rows.forEach((r, i) => {
        const el = items[i + 1];
        el.querySelector('.wz-summary__group').textContent = r.group;
        el.querySelector('.wz-summary__name').textContent = r.name;
        setMoney(el, r.pence, r.was);
        if (r.free) {
          el.querySelector('.wz-summary__price').textContent = d.freeLabel || 'Free';
          el.classList.add('is-free');
        }
        el.setAttribute('aria-label', (this.t.change || 'Change __NAME__').replace('__NAME__', r.name));
      });

      const wasTotal = rows.reduce((sum, r) => sum + (r.was || r.pence), baseWas || base);
      const saving = wasTotal - total;

      this.summaryHost.querySelector('.wz-summary__total span').textContent = d.totalLabel || '';
      this.summaryHost.querySelector('.wz-summary__total b').textContent = money(total);

      const savingBlock = this.summaryHost.querySelector('.wz-summary__saving');
      savingBlock.hidden = saving <= 0;
      if (saving > 0) {
        savingBlock.querySelector('span').textContent = d.savingLabel || '';
        savingBlock.querySelector('b').textContent = money(saving);
      }

      this.summaryHost.querySelector('.wz-summary__note').textContent = rows.length ? d.summaryNote || '' : '';


      if (this.finance && this.finance.parentElement !== this.summaryHost) {
        // Last row of the summary, so the order reads total, saving, note,
        // then finance. The saving sits directly under the total; it is the
        // product discount, not a finance figure, and the old placement
        // (finance between the two) read as if Zopa was the source of it.
        this.summaryHost.appendChild(this.finance);
      }
    }

    // A family whose options all belong to other sizes (Falcon's only wood
    // preserver is set up on the 4m x 3m) would otherwise show its label and
    // note over nothing to pick.
    syncFamilies() {
      this.root.querySelectorAll('.wz-family').forEach((fam) => {
        // A family holding the step's default swatch ("None") stays, so the
        // shopper keeps that choice even when the family's own options are
        // for other sizes.
        const options = Array.from(fam.querySelectorAll('[data-option]'));
        fam.hidden = !fam.querySelector('.wz-default') && options.length > 0 && options.every((o) => o.classList.contains('hidden'));
      });
    }

    syncFamilyInfo() {
      this.root.querySelectorAll('.wz-family').forEach((fam) => {
        const button = fam.querySelector('.wz-family__info');
        if (!button) return;
        const checked = fam.querySelector('[data-swatch-option] input[type="checkbox"]:checked');
        const swatch = checked && checked.closest('[data-swatch-option]');
        const tab = swatch && swatch.dataset.infoTab;
        button.hidden = !tab;
        if (!tab) return;
        button.dataset.infoTab = tab;
        button.setAttribute('aria-label', (this.t.moreAbout || 'More information about __NAME__').replace('__NAME__', swatch.dataset.name || ''));
      });
    }

    syncDefaults() {
      this.steps.forEach((step) => {
        const el = step.querySelector('.wz-default');
        if (!el) return;
        const empty = !this.chosen(step);
        el.classList.toggle('is-on', empty);
        el.querySelector('button').setAttribute('aria-pressed', empty ? 'true' : 'false');
      });
    }

    schedule() {
      window.clearTimeout(this.renderTimer);
      this.renderTimer = window.setTimeout(() => this.render(), 60);
    }

    nextIndex() {
      for (let i = this.index + 1; i < this.steps.length; i++) {
        if (this.visible(this.steps[i])) return i;
      }
      return this.index;
    }

    prevIndex() {
      for (let i = this.index - 1; i >= 0; i--) {
        if (this.visible(this.steps[i])) return i;
      }
      return this.index;
    }

    focusStep() {
      const step = this.steps[this.index];
      const heading = step && step.querySelector('.section-options__title');
      if (!heading) return;
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }

    stepLabel(i) {
      const total = this.live().length || this.steps.length;
      const pos = this.live().indexOf(i);
      const of = (this.t.stepOf || 'Step __N__ of __T__')
        .replace('__N__', pos > -1 ? pos + 1 : i + 1)
        .replace('__T__', total);
      return this.title(this.steps[i], i) + ', ' + of;
    }

    syncRailLabels() {
      Array.from(this.railHost.children).forEach((item, i) => {
        const step = this.steps[i];
        if (!step) return;
        let state = this.chosen(step) ? this.t.chosen : this.t.empty;
        if (item.disabled) state = this.t.locked;
        item.setAttribute('aria-label', this.stepLabel(i) + (state ? ', ' + state : ''));
        if (i === this.index) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
    }

    syncSwatchInfoLabel() {
      this.steps.forEach((step) => {
        const trigger = step.querySelector('.wz-swatchgroup__info');
        if (!trigger) return;
        const name = step.querySelector('.wz-swatchgroup__value');
        const value = name ? name.textContent.trim() : '';
        trigger.setAttribute(
          'aria-label',
          value ? (this.t.moreAbout || 'More information about __NAME__').replace('__NAME__', value) : ''
        );
      });
    }

    go(i) {
      if (i === this.index) return;
      if (i > this.index) {
        // A notice on a step behind the shopper (an option ticked later can
        // complete a rule whose trigger sits earlier) holds forward moves:
        // take them back to it. Notices ahead are caught by canLeave below.
        const behind = this.steps.findIndex((step, n) => n < this.index && step.querySelector('.pcfg-required'));
        if (behind > -1) {
          this.go(behind);
          this.flag(behind);
          return;
        }
      }
      if (i > this.index && !this.canLeave(this.index)) {
        this.flag(this.index);
        return;
      }
      if (i > this.index) {
        for (let s = this.index; s < i; s++) {
          if (!this.canLeave(s)) {
            this.go(s);
            this.flag(s);
            return;
          }
        }
      }
      this.index = Math.max(0, Math.min(this.steps.length - 1, i));
      this.render();
      if (this.scrollable) this.scrollable.scrollTo({ top: 0, behavior: 'smooth' });
      this.focusStep();
    }

    flag(i) {
      const step = this.steps[i];
      if (!step) return;
      step.classList.add('is-wizard-missing');
      window.setTimeout(() => step.classList.remove('is-wizard-missing'), 1600);
    }

    money(pence) {
      try {
        if (window.theme && theme.Currency && theme.Currency.formatMoney) {
          const format = theme.settings.currencyCodeEnabled
            ? theme.settings.moneyWithCurrencyFormat
            : theme.settings.moneyFormat;
          return theme.Currency.formatMoney(pence, format);
        }
      } catch (e) {
      }
      return null;
    }

    syncTotal() {
      const from = this.submit && this.submit.querySelector('.btn-text');
      if (!from) return;

      const base = (this.root.activeVariant && this.root.activeVariant.price) || 0;
      let gross = base;

      this.root.querySelectorAll('[data-option]:not(.hidden):not([data-gift])').forEach((o) => {
        const input = o.querySelector('input[type="checkbox"], input[type="radio"]');
        if (input && input.checked) gross += parseInt(o.dataset.price, 10) || 0;
      });

      const pretty = this.money(Math.max(0, gross - this.freeAllowance()));
      const full = from.textContent.trim();
      const match = full.match(/^([^\d]*)(\d[\d.,\s]*)/);
      const to = this.navHost.querySelector('[data-wizard-total]');

      if (pretty && match) {
        const raw = (match[1] + match[2]).trim();
        if (raw !== pretty) from.textContent = full.replace(raw, pretty);
      }

      if (to) to.textContent = pretty || full.split(' - ')[0].trim();
    }

    // Mixed treatment-or-paint cards: while the current size offers both a
    // service and colours, hide the colour header and the host's note (the
    // theme has no global [hidden] rule and the header is a flex row) and
    // price each colour family from its cheapest visible swatch, "from" when
    // they differ. A size with colours only gets the ordinary card back.
    syncMixedCards() {
      this.root.querySelectorAll('[data-wizard-mixed]').forEach((sec) => {
        const shown = (sel) => Array.from(sec.querySelectorAll(sel)).some((o) => !o.classList.contains('hidden'));
        const both = shown('[data-checkbox-option]') && shown('[data-swatch-option]');
        const preview = sec.querySelector('.wz-swatchgroup__preview');
        if (preview) preview.style.display = both ? 'none' : '';
        sec.querySelectorAll('.section-options__header p').forEach((p) => { p.style.display = both ? 'none' : ''; });
        sec.querySelectorAll('.wz-family__price').forEach((tag) => {
          const fam = tag.closest('.wz-family');
          const prices = both && fam
            ? Array.from(fam.querySelectorAll('[data-swatch-option]:not(.hidden)'))
              .map((o) => parseInt(o.dataset.price, 10))
              .filter((n) => !isNaN(n))
            : [];
          const pretty = prices.length ? this.money(Math.min.apply(null, prices)) : '';
          // Spaced like the service card beside it ("+ £199.99").
          tag.textContent = pretty ? (new Set(prices).size > 1 ? 'from ' : '') + '+ ' + pretty : '';
        });
      });
    }

    render() {
      this.syncMixedCards();
      const live = this.live();

      if (live.length && live.indexOf(this.index) === -1) {
        this.index = live[0];
      }

      this.steps.forEach((step, i) => {
        const isCurrent = i === this.index;
        step.classList.toggle('is-wizard-current', isCurrent);
        step.classList.toggle('is-wizard-hidden', !isCurrent);

        if (step.wizardCount) {
          const pos = live.indexOf(i);
          step.wizardCount.textContent = pos > -1 ? 'Step ' + (pos + 1) + ' of ' + live.length : '';
        }
      });

      // Arriving on (or re-rendering) the Delivery day step: it asks MGP
      // only when the postcode or the build changed since it last did.
      if (this.isDay(this.steps[this.index]) && this.root.padStep) {
        try {
          this.root.padStep.enter();
        } catch (e) {
          // The day step is never allowed to break the wizard.
        }
      }

      const railItems = Array.from(this.railHost.children);
      // Numbered among the steps shown, as the "Step N of T" count is: a
      // step with nothing to offer for this product or size is hidden, and
      // its fixed number left gaps (2, 3, 4, 5, 6, 8).
      const shown = this.live();
      railItems.forEach((item, i) => {
        const step = this.steps[i];
        const num = item.querySelector('.wizard-rail__num');
        const pos = shown.indexOf(i);
        if (num && pos > -1) num.textContent = (pos + 1) + '.';
        const reachable = this.visible(step) && (i <= this.index || this.canLeaveAllBefore(i));
        item.classList.toggle('is-current', i === this.index);
        item.classList.toggle('is-done', i < this.index);
        item.classList.toggle('is-chosen', this.chosen(step));
        item.classList.toggle('is-locked', !reachable && i !== this.index);
        item.hidden = !this.visible(step);
        item.disabled = !reachable && i !== this.index;
      });

      const last = this.isLast();
      // Continue stays pressable when the notice is behind the shopper, so
      // go() can take them back to it.
      const blocked = !this.canLeave(this.index);

      this.root.classList.toggle('is-wizard-final', last);

      if (last) this.reachedEnd = true;
      this.root.classList.toggle('has-reached-end', !!this.reachedEnd);
      if (this.root.prompt && this.root.prompt.setFinal) this.root.prompt.setFinal(last);

      const back = this.navHost.querySelector('[data-wizard-back]');
      const next = this.navHost.querySelector('[data-wizard-next]');
      if (back) {
        back.hidden = !live.length || this.index === live[0];
        const backText = back.querySelector('.btn-text');
        if (backText) backText.textContent = this.t.back || 'Back';
      }

      const nextText = next && next.querySelector('.btn-text');
      if (nextText) {
        const step = this.steps[this.index];
        const skippable = step && !this.required(step) && !this.chosen(step);
        nextText.textContent = skippable
          ? (this.t.skip || 'Skip __NAME__').replace('__NAME__', this.title(step, this.index))
          : this.t.continue || 'Continue';
      }
      if (next) {
        next.hidden = last;
        next.disabled = blocked;
        next.setAttribute('aria-disabled', blocked ? 'true' : 'false');
      }

      this.syncSelected();
      this.syncDefaults();
      this.syncFreeGroups();
      this.syncSummary();
      this.syncFamilies();
      this.syncFamilyInfo();
      this.syncRailLabels();
      this.syncSwatchInfoLabel();

      const hint = this.navHost.querySelector('[data-wizard-hint]');
      if (hint) {
        const current = this.steps[this.index];
        const needsFix = current && current.querySelector('.pcfg-required');
        hint.textContent = blocked
          ? needsFix
            ? this.t.requiredToContinue || 'See the note above to continue'
            : this.t.chooseToContinue || 'Choose an option to continue'
          : '';
      }

      this.syncTotal();
    }

    canLeaveAllBefore(i) {
      for (let s = 0; s < i; s++) {
        if (!this.canLeave(s)) return false;
      }
      return true;
    }
  }

  const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const OPEN_MIN = 8 * 60 + 30;
  const CLOSE_MIN = 17 * 60;

  class SalesPrompt {
    constructor(root) {
      this.root = root;
      this.help = root.querySelector('[data-wizard-help]');
      if (!this.help) return;

      const raw = root.querySelector('[data-wizard-strings]');
      try {
        this.s = JSON.parse(raw.textContent);
      } catch (e) {
        this.s = {};
      }

      this.override = new URLSearchParams(window.location.search).get('hours');
      this.applyHours();
    }

    isOpen() {
      if (this.override === 'open') return true;
      if (this.override === 'closed') return false;
      const d = new Date();
      const day = d.getDay();
      const mins = d.getHours() * 60 + d.getMinutes();
      return day >= 1 && day <= 5 && mins >= OPEN_MIN && mins < CLOSE_MIN;
    }

    nextOpening() {
      const now = new Date();
      for (let i = 0; i < 8; i++) {
        const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i, 8, 30, 0);
        const day = d.getDay();
        if (day < 1 || day > 5) continue;
        if (d > now) {
          if (i === 0) return 'today at 8:30am';
          if (i === 1) return 'tomorrow at 8:30am';
          return DAY_NAMES[day] + ' at 8:30am';
        }
      }
      return 'Monday at 8:30am';
    }

    setFinal(isFinal) {
      this.isFinal = isFinal;
      const heading = this.help.querySelector('[data-wizard-help-heading]');
      if (heading) heading.textContent = isFinal ? this.s.helpTitleFinal : this.s.helpTitle;
      this.applyHours();
    }

    applyHours() {
      const now = this.isOpen();

      const hours = this.help.querySelector('[data-wizard-hours]');
      if (hours) hours.textContent = now ? this.s.hoursOpen : (this.s.hoursClosed || '').replace('__NEXT__', this.nextOpening());

      const tel = this.help.querySelector('[data-wizard-tel]');
      if (tel) tel.hidden = !now;

      const copy = this.help.querySelector('[data-wizard-help-copy]');
      if (copy) copy.innerHTML = this.isFinal ? this.s.helpFinal : now ? this.s.helpOpen : this.s.helpClosed;
    }
  }
  function init() {
    document.querySelectorAll('product-configurator[data-wizard]').forEach((root) => {
      if (root.wizard) return;
      root.wizard = new ConfiguratorWizard(root);
      root.prompt = new SalesPrompt(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
